import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Eye, EyeOff, Lock, Pencil } from "lucide-react";

import { Button, Input, Status, Surface } from "../../../../components/ui";
import { numberStatus } from "../statutoryView";

// Government numbers (CD-S1, migrated in UI-3E). One list for three places: an employee's numbers in HR, an
// employee's own numbers in My HR, and the business's own registration numbers. Numbers are masked. A full number is
// fetched only when someone asks to see it, is shown on this screen only, and is hidden again after a short while. One
// number is edited at a time. What a person may do is decided by the server; this panel only reflects the answer.

const REVEAL_SECONDS = 30;

function StatusWord({ recorded }) {
  return <Status tone={recorded ? "neutral" : "warning"} icon={!recorded} className="text-body-sm">{numberStatus({ recorded })}</Status>;
}

function NumberRow({ item, labels, canReveal, canEdit, adapter, onSaved, announce, suggestion }) {
  const text = labels[item.agency] || { name: item.agency, label: item.agency, help: "" };
  const inputId = useId();
  const errorId = useId();
  const [revealed, setRevealed] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const hideTimer = useRef(null);

  const hide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = null;
    setRevealed(null);
  }, []);

  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);
  // A number that changed underneath (after a save) is never left showing its old value.
  useEffect(() => { hide(); }, [item.masked, item.recorded, hide]);

  const reveal = async () => {
    setError("");
    setRevealing(true);
    try {
      const answer = await adapter.reveal(item.agency);
      setRevealed(answer?.value || null);
      hideTimer.current = setTimeout(() => { hide(); announce(`${text.label} is hidden again.`); }, REVEAL_SECONDS * 1000);
      announce(`${text.label} is shown. It hides again in ${REVEAL_SECONDS} seconds.`);
    } catch (err) {
      setError(err?.serverMessage || `The ${text.label} could not be shown. Try again.`);
    } finally {
      setRevealing(false);
    }
  };

  const startEdit = () => {
    hide();
    setError("");
    setDraft("");
    setEditing(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    const value = draft.trim();
    if (!value && !item.recorded) {
      setError(`Enter the ${text.label}.`);
      return;
    }
    if (value && (!/^[0-9\s-]+$/.test(value) || value.replace(/[^0-9]/g, "").length < 6)) {
      setError("Use digits and dashes only, with at least 6 digits.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const answer = await adapter.save({ [item.agency]: value || null });
      setEditing(false);
      setDraft("");
      announce(value ? `${text.label} saved.` : `${text.label} removed.`);
      onSaved(answer);
    } catch (err) {
      setError(err?.serverMessage || `The ${text.label} could not be saved. Try again.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="px-4 py-3.5" aria-label={text.label}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-panel-title text-foreground">{text.label}</span>
            <StatusWord recorded={item.recorded} />
          </div>
          <p className="mt-0.5 text-meta text-tertiary">{text.help}</p>
          {item.recorded && "masked" in item ? (
            <p className="mt-1.5 text-body tabular-nums tracking-wide text-foreground" aria-live="off">
              {revealed ? (
                <span data-testid={`number-${item.agency}`}>{revealed}</span>
              ) : (
                <span data-testid={`number-${item.agency}`} aria-label={`${text.label} ending in ${String(item.masked || "").slice(-4)}`}>
                  {item.masked}
                </span>
              )}
            </p>
          ) : null}
        </div>

        {!editing && (canReveal || canEdit) ? (
          <div className="flex shrink-0 flex-wrap gap-2">
            {canReveal && item.recorded ? (
              revealed ? (
                <Button size="sm" icon={EyeOff} onClick={() => { hide(); announce(`${text.label} is hidden.`); }}>Hide</Button>
              ) : (
                <Button size="sm" icon={Eye} loading={revealing} onClick={reveal} aria-label={`Show the full ${text.label}`}>Show</Button>
              )
            ) : null}
            {canEdit ? (
              <Button size="sm" icon={Pencil} onClick={startEdit} aria-label={item.recorded ? `Change the ${text.label}` : `Add the ${text.label}`}>
                {item.recorded ? "Change" : "Add"}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {editing ? (
        <form onSubmit={save} className="mt-3 rounded-panel border border-subtle bg-surface-sunken p-3" noValidate>
          <label htmlFor={inputId} className="block text-label text-secondary">
            {item.recorded ? `New ${text.label}` : text.label}
          </label>
          <Input
            id={inputId}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            autoFocus
            maxLength={24}
            value={draft}
            onChange={(event) => { setDraft(event.target.value); if (error) setError(""); }}
            aria-invalid={error ? "true" : undefined}
            aria-describedby={error ? errorId : undefined}
            className="mt-1.5 max-w-sm tabular-nums"
            placeholder="Digits and dashes"
          />
          <p className="mt-1.5 text-meta text-tertiary">
            {item.recorded
              ? "The number on file stays hidden. Type the new number, or leave this empty and save to remove it."
              : "Copy it from the official document or card."}
          </p>
          {suggestion && !item.recorded ? (
            <Button size="sm" variant="quiet" className="mt-2" onClick={() => setDraft(suggestion.value)}>{suggestion.label}</Button>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="submit" size="sm" variant="primary" loading={saving}>{saving ? "Saving…" : "Save number"}</Button>
            <Button size="sm" variant="quiet" disabled={saving} onClick={() => { setEditing(false); setError(""); }}>Cancel</Button>
          </div>
        </form>
      ) : null}

      {error ? <p id={errorId} role="alert" className="mt-2 text-body-sm text-danger">{error}</p> : null}
    </li>
  );
}

export default function GovernmentNumbersPanel({
  adapter,
  labels,
  title = "Government numbers",
  subtitle = "",
  privacyNote = "Numbers are hidden by default. Showing or changing one is recorded.",
  restrictedText = "Government numbers are not part of your role. Ask the business owner or an HR manager if you need one updated.",
  readOnlyNote = "",
  suggestionFor = null,
  reloadKey = 0,
}) {
  const [state, setState] = useState({ loading: true, error: "", restricted: false, data: null });
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const data = await adapter.load();
      setState({ loading: false, error: "", restricted: false, data });
    } catch (err) {
      if (err?.status === 403) setState({ loading: false, error: "", restricted: true, data: null });
      else setState({ loading: false, error: err?.serverMessage || "The numbers could not be loaded.", restricted: false, data: null });
    }
  }, [adapter]);

  useEffect(() => { load(); }, [load, reloadKey]);

  const { loading, error, restricted, data } = state;
  const rows = data?.numbers || data?.registrations || [];
  const canReveal = Boolean(data?.canReveal ?? data?.isOwner);
  const canEdit = Boolean((data?.canEdit ?? data?.isOwner) && adapter.save);
  const missing = rows.filter((row) => !row.recorded).length;

  return (
    <Surface
      as="section"
      aria-label={title}
      toolbar={(
        <div>
          <h2 className="text-panel-title text-foreground">{title}</h2>
          {subtitle ? <p className="text-body-sm text-secondary">{subtitle}</p> : null}
        </div>
      )}
    >
      {loading ? (
        <p className="p-4 text-body-sm text-tertiary">Loading…</p>
      ) : restricted ? (
        <div className="flex items-start gap-3 p-4 text-body-sm text-secondary">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-tertiary" aria-hidden="true" />
          <p>{restrictedText}</p>
        </div>
      ) : error ? (
        <div className="p-4">
          <p role="alert" className="text-body-sm text-danger">{error}</p>
          <Button size="sm" className="mt-3" onClick={load}>Try again</Button>
        </div>
      ) : (
        <>
          <p className="border-b border-subtle bg-surface-sunken px-4 py-2.5 text-body-sm text-secondary">
            {missing === 0
              ? "All numbers are recorded."
              : `${missing} of ${rows.length} ${missing === 1 ? "number is" : "numbers are"} missing.`}
            {" "}
            {canReveal || canEdit ? privacyNote : readOnlyNote}
          </p>
          <ul className="divide-y divide-subtle">
            {rows.map((item) => (
              <NumberRow
                key={item.agency}
                item={item}
                labels={labels}
                canReveal={canReveal}
                canEdit={canEdit}
                adapter={adapter}
                announce={setMessage}
                suggestion={suggestionFor ? suggestionFor(item, data) : null}
                onSaved={(answer) => {
                  if (answer?.numbers || answer?.registrations) setState((current) => ({ ...current, data: { ...current.data, ...answer } }));
                  else load();
                }}
              />
            ))}
          </ul>
          <p role="status" aria-live="polite" className={message ? "border-t border-subtle px-4 py-2.5 text-body-sm text-secondary" : "sr-only"}>
            {message}
          </p>
        </>
      )}
    </Surface>
  );
}
